/**
 * Converte `usuario.arvoreMenu` em árvore de menu.
 * Fluxo: unwrap da raiz "Sidebar" → ignora nós "Index" → { id, label, route, icon, children }.
 */
import { ArvoreMenuUsuario, SidebarItem } from './sidebar.types';
import { Usuario } from '@/modules/auth/models/usuario.model';
import { isRouteMatch, normalizePath, normalizeRoute } from './sidebar.utils';

const ICON_ALIASES: Record<string, string> = {
    Home: 'home',
    House: 'home',
    Dashboard: 'dashboard',
    Settings: 'settings',
    Person: 'person',
    People: 'group',
    Description: 'description',
    Event: 'event',
    Logout: 'logout',
    Build: 'build',
};

export function readArvoreMenuFromUsuario(usuario: Usuario | null): ArvoreMenuUsuario[] {
    if (!usuario) {
        return [];
    }
    return usuario.arvoreMenu ?? usuario.ArvoreMenu ?? [];
}

export function mapArvoreMenuToTree(nodes: ArvoreMenuUsuario[]): SidebarItem[] {
    return unwrapSidebarRoot(nodes).flatMap((node, index) => toSidebarItem(node, `${index}`));
}

export function flattenSidebarItems(items: SidebarItem[]): SidebarItem[] {
    return items.flatMap((item) => [item, ...flattenSidebarItems(item.children)]);
}

export function omitAuditoriaItems(items: SidebarItem[]): SidebarItem[] {
    return items.flatMap((item) => {
        const children = omitAuditoriaItems(item.children);
        if (isAuditoriaRoute(item.route)) {
            return children;
        }
        return [{ ...item, children }];
    });
}

export function findActiveItemId(items: SidebarItem[], currentUrl: string): string | undefined {
    const path = normalizePath(currentUrl);
    const flat = flattenSidebarItems(items);
    const exact = flat.find((item) => !!item.route && isRouteMatch(path, item.route));
    if (exact) {
        return exact.id;
    }

    return flat.find(
        (item) => !!item.route && item.route !== '/' && path.startsWith(item.route)
    )?.id;
}

export function isAuditoriaRoute(route: string): boolean {
    return !!route && (route === '/auditoria' || route.startsWith('/auditoria/'));
}

function unwrapSidebarRoot(nodes: ArvoreMenuUsuario[]): ArvoreMenuUsuario[] {
    return nodes.flatMap((node) => {
        const children = readChildren(node);
        const isSidebarWrapper = (node.nome ?? '').trim().toLowerCase() === 'sidebar';
        return isSidebarWrapper && children.length > 0 ? children : [node];
    });
}

function toSidebarItem(node: ArvoreMenuUsuario, path: string): SidebarItem[] {
    const { label, icon } = parseLabelAndIcon(node.nome);
    const rawChildren = readChildren(node);
    const children = rawChildren.flatMap((child, index) => toSidebarItem(child, `${path}-${index}`));
    const route = normalizeRoute(node.url);
    const isIndexNode = label.toLowerCase() === 'index';
    const hasContent = !!(label || route || rawChildren.length);

    if (isIndexNode || !hasContent) {
        return children;
    }

    const nodeId = `arvore-${path}`;
    return [
        {
            id: route || nodeId,
            label: label || 'Menu',
            route,
            icon: toMaterialIcon(icon, rawChildren.length > 0),
            children,
        },
    ];
}

function readChildren(node: ArvoreMenuUsuario): ArvoreMenuUsuario[] {
    if (Array.isArray(node.arvoreDeMenu)) {
        return node.arvoreDeMenu;
    }
    if (Array.isArray(node.ArvoreDeMenu)) {
        return node.ArvoreDeMenu;
    }
    return [];
}

function parseLabelAndIcon(nome?: string): { label: string; icon: string } {
    const raw = (nome ?? '').trim();
    if (!raw) {
        return { label: '', icon: '' };
    }
    const [labelPart, iconPart] = raw.split('$', 2);
    return {
        label: (labelPart ?? '').trim(),
        icon: (iconPart ?? '').trim(),
    };
}

function toMaterialIcon(icon: string, hasChildren: boolean): string {
    const fallback = hasChildren ? 'folder' : 'article';
    const raw = (icon ?? '').trim();
    if (!raw) {
        return fallback;
    }
    if (/^[a-z0-9_]+$/.test(raw)) {
        return raw;
    }

    const noSpaces = raw.replace(/\s+/g, '');
    const withoutIconSuffix = noSpaces.replace(/Icon$/i, '');
    const withoutVariant = withoutIconSuffix.replace(/(Outlined|Rounded|Sharp|TwoTone|Filled)$/i, '');

    if (ICON_ALIASES[withoutVariant]) {
        return ICON_ALIASES[withoutVariant];
    }

    const snake = withoutVariant
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/-+/g, '_')
        .toLowerCase();
    const cleaned = snake.replace(/_(outlined|rounded|sharp|two_tone|filled)$/i, '');
    return cleaned || fallback;
}
